import {ClockInResourceProviderType} from "../../timekeeping/angular/models/planned-resource.model";
import {TimekeepingFrequencyType} from "../../timekeeping/angular/models/payment-cycle.model";
import {ResourceScopeType} from "../../agents/types/resourceScope.type";
import {WeekDaysEnum} from "../../commons/angular/enums/week-days.enum";

export enum GapClassification {
  UNKNOWN = 'UNKNOWN',
  STOP = 'STOP',
  MOVE = 'MOVE',
}

export enum ActionPostWindowPolicyType {
  Block = "BLOCK",
  Warn = "WARN",
  Allow = "ALLOW"
}

export enum ClockInOnResourceEndType {
  Disabled = "DISABLED",
  Enabled = "ENABLED",
  EnabledIfAllTasksCompleted = "ENABLED_IF_ALL_TASKS_COMPLETED"
}

export enum AgentMobileUxDisplayOption {
  Show = "SHOW",
  Hide = "HIDE",
  Warn = "WARN"
}

export enum AgentShiftCompensationProviderType {
  None = "NONE",
  Mondelez = "MONDELEZ",
  Standard = "STANDARD"
}

export interface AgentActualDataDetectionPreferencesModel {
  stopRadiusMeters: number;
  minStopDurationSeconds: number;
  minStopThresholdSeconds: number;
  maxGapDurationSeconds: number;
  gapClassification: GapClassification;
  maxStopSpeedMps: number;
  minStopPoints: number;
}

export interface AgentTimekeepingPreferencesModel {
  reportFrequency: TimekeepingFrequencyType;
  requireAgentAcknowledgmentBeforeApproval: boolean;
  blockApprovalOnInfeasibleTimeCards: boolean;
  allowManualStopDistanceEdit: boolean;
  driveTimeThresholdMillis: number;
  distanceDeviationThresholdPercentage: number;
  serviceTimeDeviationThresholdPercentage: number;
  driveTimeDeviationThresholdPercentage: number;
  adjustedStartTimeDeviationThresholdMillis: number;
  adjustedEndTimeDeviationThresholdMillis: number;
  adjustedDistanceDeviationThresholdMeters: number;
  geoTimeMismatchThresholdMillis: number;
  dwellTimeThresholdMillis: number;
  actualDataDetectionPreferences: AgentActualDataDetectionPreferencesModel;
}

export interface AgentClockInProviderPreferencesModel {
  provider: ClockInResourceProviderType;
  enforceResourceBasedLogin: boolean;
  maxConcurrentClockInResources: number;
  resourceVisibilityTimeWindowNotBeforeHours: number;
  resourceVisibilityTimeWindowNotAfterHours: number;
  autoAssignPlannedWorkOnClockIn: boolean;
  autoClockOutOnResourceEnd: ClockInOnResourceEndType;
  resourceScope: ResourceScopeType;
}

export interface AgentClockInPreferencesModel {
  allowClockInOutsideShift: boolean;
  clockInPreWindowMinutes: number;
  clockInPostWindowMinutes: number;
  actionPostWindowPolicy: ActionPostWindowPolicyType;
}

export interface AgentShiftAssignmentOverlapSettingsModel {
  allowShiftVsShift: boolean;
  allowShiftVsAvailability: boolean;
  allowShiftVsLeave: boolean;
}

export interface AgentShiftAssignmentPreferencesModel {
  overlapSettings: AgentShiftAssignmentOverlapSettingsModel;
  blockAssignmentDuringLeave: boolean;
  blockAssignmentForInactiveAgent: boolean;
  maxRecordsPerAgentPerDay: number;
  minGapBetweenRecordsMinutes: number;
}

export interface AgentInShiftBehaviorPreferencesModel {
  requireClockInBeforeTaskStart: boolean;
  minBreakDurationMinutes: number;
  maxShiftLengthHours: number;
  maxHoursPer7Days: number;
  maxHoursPerDay: number;
  maxWorkDaysPerWeek: number;
  dailyHoursTarget: number;
  weeklyHoursTarget: number;
  hoursGoalTolerancePercent: number;
  countOnlyPaidHoursInGoals: boolean;
  includeApprovedLeaveInWeeklyGoal: boolean;
  breakTypeIds: string[];
}

export interface AgentNotificationPreferencesModel {
  notifyOnShiftViolation: boolean;
  notifyOnTaskOutsideShift: boolean;
  surfaceViolationsInReport: boolean;
  notifyOnTimecardEdits: boolean;
  notifyOnTimecardEditsDelaySeconds: number;
  notifyOnTimecardApproved: boolean;
  notifyOnTimecardUnApproved: boolean;
  notifyTimecardAcknowledgeReminder: boolean;
  timecardAcknowledgeReminderTime: string;
  timecardAcknowledgeReminderDayOfWeek: WeekDaysEnum;
  timecardAcknowledgeReminderNotBeforeDays: number;
  timecardAcknowledgeReminderNotAfterDays: number;
}

export interface AgentMobileUxPreferencesModel {
  displayTasksOutsideShift: AgentMobileUxDisplayOption;
  warnAgentWithoutShift: boolean;
  startOfTheWeekDay: WeekDaysEnum;
}

export interface AgentRecordRestrictionOverlapSettingsModel {
  allowAvailabilityVsLeave: boolean;
  allowAvailabilityVsAvailability: boolean;
  allowLeaveVsLeave: boolean;
}

export interface AgentRecordRestrictionPreferencesModel {
  overlapSettings: AgentRecordRestrictionOverlapSettingsModel;
  maxAvailabilityAdvanceDays: number;
  minimumAvailabilityLengthHours: number;
  allowAgentsToSubmitTimeOff: boolean;
}

export interface AgentShiftCompensationPreferencesModel {
  enabled: boolean;
  provider: AgentShiftCompensationProviderType;
  distanceThreshold: number;
  tolerancePercentage: number;
  maxLeaveTimePerDayInMinutes: number;
  maxBreakTimePerDayInMinutes: number;
}

export interface AgentShiftRestrictionsModel {
  agentTimekeepingPreferences: AgentTimekeepingPreferencesModel;
  agentClockInProviderPreferences: AgentClockInProviderPreferencesModel;
  agentClockInPreferences: AgentClockInPreferencesModel;
  agentShiftAssignmentPreferences: AgentShiftAssignmentPreferencesModel;
  agentInShiftBehaviorPreferences: AgentInShiftBehaviorPreferencesModel;
  agentNotificationPreferences: AgentNotificationPreferencesModel;
  agentMobileUxPreferences: AgentMobileUxPreferencesModel;
  agentRecordRestrictionPreferences: AgentRecordRestrictionPreferencesModel;
  agentShiftCompensationPreferences: AgentShiftCompensationPreferencesModel;
}
